/**
 * 缓存初始化模块
 * 提供页面首次加载时的异步缓存清理功能
 */

import { clearExpired, DEFAULT_CONFIG } from './indexedDbCache.js';

// 清理标志的存储键名
const CLEANUP_FLAG_KEY = 'ocr_cache_cleanup_executed';

/**
 * 异步清理过期缓存
 * 使用 requestIdleCallback 或 setTimeout 避免阻塞主线程
 * @param {Object} config - 缓存配置
 * @returns {Promise<void>}
 */
export async function cleanupExpiredCacheAsync(config = {}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  
  try {
    const deletedCount = await clearExpired(mergedConfig);
    if (deletedCount > 0) {
      console.log(`[Cache] 已清理 ${deletedCount} 个过期缓存条目`);
    } else {
      console.log('[Cache] 无过期缓存需要清理');
    }
  } catch (error) {
    console.error('[Cache] 清理过期缓存失败:', error);
  }
}

/**
 * 初始化缓存清理（仅在首次页面加载时执行）
 * 该函数是非阻塞的，会立即返回
 * @param {Object} config - 缓存配置
 */
export function initializeCacheCleanup(config = {}) {
  let cleanupExecuted = false;
  
  // 尝试检查是否已执行过清理（处理 sessionStorage 不可用的情况）
  try {
    cleanupExecuted = sessionStorage.getItem(CLEANUP_FLAG_KEY) === 'true';
  } catch (error) {
    console.warn('[Cache] 无法访问 sessionStorage，将继续执行清理:', error);
  }
  
  if (cleanupExecuted) {
    return;
  }
  
  // 尝试标记清理已执行
  try {
    sessionStorage.setItem(CLEANUP_FLAG_KEY, 'true');
  } catch (error) {
    console.warn('[Cache] 无法设置清理标志，清理可能重复执行:', error);
  }
  
  // 使用 requestIdleCallback 在浏览器空闲时执行，或使用 setTimeout 延迟
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => {
      cleanupExpiredCacheAsync(config).catch(error => {
        console.error('[Cache] 异步清理失败:', error);
      });
    }, { timeout: 5000 }); // 5秒超时，确保即使不空闲也会执行
  } else {
    // 回退方案：延迟 1 秒执行，避免阻塞渲染
    setTimeout(() => {
      cleanupExpiredCacheAsync(config).catch(error => {
        console.error('[Cache] 异步清理失败:', error);
      });
    }, 1000);
  }
}

/**
 * 重置清理标志（主要用于测试）
 */
export function resetCleanupFlag() {
  sessionStorage.removeItem(CLEANUP_FLAG_KEY);
}

export default {
  initializeCacheCleanup,
  cleanupExpiredCacheAsync,
  resetCleanupFlag,
};